import React, { useState, useEffect } from 'react';
import SFWelcomeMat from '@salesforce/design-system-react/components/welcome-mat';
import Card from '@salesforce/design-system-react/components/card';
import { useTranslation } from 'react-i18next';
import Button from '@salesforce/design-system-react/components/button';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import Pill from '@salesforce/design-system-react/components/pill';
import CQCurrentOrgInfoModal from 'components/cq-current-org-info-modal';
import { IApplicableModules, IExtensionInfoMessage, IInstalledPackage, IUpgradeSchema } from 'interfaces/IUpgradeSchema.interface';
import { CQAppConstant } from 'app-constants';
import { IOrgInfo } from 'interfaces/IModal.interface';
import { getPackagesWithUnsatisfiedDependencies, getSelectedDependents, withDependenciesSelected, getUnsatisfiedDependencyMessage } from 'utils/dependencies.utils';

interface ModulesProps {
    freshModule: IApplicableModules;
    onInstall: (selectedModules: { [key: string]: boolean }, selectedVersions: { [key: string]: string }) => void;
    onCloseMat?: () => void;
    availableModules?: string[];
    orgInfo: IOrgInfo;
    errorMessage?: string;
    isPartialFresh?: boolean;
    installedPackages?: IInstalledPackage[];
}

const WelcomeMat: any = SFWelcomeMat;

function CQWelcomeMat(props: ModulesProps) {
    const { t } = useTranslation();
    const isVersionLocked = props.isPartialFresh || false;
    // Only the packages the user ticked themselves. What is actually selected (`selectedModules`,
    // derived below) also includes their dependencies, so unticking a package drops the dependencies
    // it pulled in, unless another selected package still needs them or the user ticked them too.
    const [userPickedModules, setUserPickedModules] = useState<{ [key: string]: boolean }>({});
    const [selectedVersion, setSelectedVersion] = useState<string>('');
    const [isOrgInfoModalOpen, setIsOrgInfoModalOpen] = useState<boolean>(false);

    // Get all available versions across all packages, paired with their display versionName.
    // Backend already filters to top n versions and sorts descending (newest first), so we
    // preserve that order by iterating freshInstalls in sequence and deduplicating by
    // finalVersion — avoiding Map→Array conversion which loses insertion order guarantees.
    const getAvailableVersions = (): { finalVersion: string; versionName: string }[] => {
        const freshInstalls = props.freshModule?.applicableFreshInstalls || [];
        const uniqueVersions = new Set<string>();
        const versions: { finalVersion: string; versionName: string }[] = [];
        freshInstalls.forEach((install: IUpgradeSchema) => {
            if (!uniqueVersions.has(install.finalVersion)) {
                uniqueVersions.add(install.finalVersion);
                versions.push({
                    finalVersion: install.finalVersion,
                    versionName: install.versionName || install.finalVersion
                });
            }
        });
        return versions;
    };

    // Get packages available for the selected version
    const getPackagesForSelectedVersion = (): IUpgradeSchema[] => {
        const freshInstalls = props.freshModule?.applicableFreshInstalls || [];
        return freshInstalls.filter(
            (install: IUpgradeSchema) => install.finalVersion === selectedVersion
        );
    };

    const handleVersionChange = (version: string) => {
        setSelectedVersion(version);

        // When version changes, reset to clean state - pre-check ComplianceQuest and packages with required: true
        const freshInstalls = props.freshModule?.applicableFreshInstalls || [];
        const versionPackages = freshInstalls.filter(
            (install: IUpgradeSchema) => install.finalVersion === version
        );
        // Get unique package names for the selected version
        const uniquePackages = new Set(versionPackages.map((install) => install.packageName));

        // Reset selection to CQ Core and required packages for the new version
        setUserPickedModules(() => {
            const newSelection: { [key: string]: boolean } = {};
            uniquePackages.forEach((packageName) => {
                const schema = versionPackages.find((install) => install.packageName === packageName);
                // CQ Core is always selected; packages with required: true are also pre-checked
                newSelection[packageName] =
                    packageName === CQAppConstant.COMPLIANCEQUEST ||
                    schema?.required === true;
            });
            return newSelection;
        });
    };

    // Initialize selected version (defaults to latest) and selected modules based on
    // available fresh installs. Reuses getAvailableVersions()/handleVersionChange() instead
    // of re-deriving the same version/package lookups.
    useEffect(() => {
        const versions = getAvailableVersions();
        const latestVersion = versions[0]?.finalVersion || '';
        handleVersionChange(latestVersion);
    }, [props.freshModule]);

    const availableVersions = getAvailableVersions();
    const packagesForSelectedVersion = getPackagesForSelectedVersion();
    const selectedModules = withDependenciesSelected(userPickedModules, packagesForSelectedVersion);

    // A package is disabled if:
    // - It is ComplianceQuest (always locked)
    // - It has required: true in its schema
    const isDisabledModule = (packageName: string): boolean => {
        if (packageName === CQAppConstant.COMPLIANCEQUEST) return true;
        const schema = packagesForSelectedVersion.find(
            (install) => install.packageName === packageName
        );
        return schema?.required === true;
    };

    const handleModuleSelection = (moduleName: string, checked: boolean) => {
        // Prevent deselecting locked modules (ComplianceQuest or required: true)
        if (!checked && isDisabledModule(moduleName)) {
            return;
        }
        
        // A package that another selected package depends on can't be deselected
        if (!checked && getSelectedDependents(moduleName, selectedModules, packagesForSelectedVersion).length > 0) {
            return;
        }

        // Dependencies are derived from the user's picks, so they follow select and deselect
        setUserPickedModules(prev => ({ ...prev, [moduleName]: checked }));
    };

    // Builds the version map (every selected package pinned to the single selected version)
    // that gets handed off to props.onInstall once the user confirms in the org info modal.
    // Iterates packagesForSelectedVersion (not selectedModules keys) to guarantee only
    // packages that actually exist for the selected version are included — preventing stale
    // selectedModules state from sending invalid package/version combinations to the backend.
    const getSelectedVersionsForInstall = (): { [key: string]: string } => {
        const selectedVersionsForInstall: { [key: string]: string } = {};
        packagesForSelectedVersion.forEach((install: IUpgradeSchema) => {
            if (selectedModules[install.packageName]) {
                selectedVersionsForInstall[install.packageName] = selectedVersion;
            }
        });
        return selectedVersionsForInstall;
    };

    // Recomputes the list of selected package display names whenever a checkbox is toggled.
    // Derived from packagesForSelectedVersion (rather than all of selectedModules' keys) so
    // this always stays scoped to the packages currently shown for the selected version.
    const selectedPackageNames = packagesForSelectedVersion
        .filter((install: IUpgradeSchema) => selectedModules[install.packageName])
        .map((install: IUpgradeSchema) => install.packageName);

    const unsatisfiedDependencies = getPackagesWithUnsatisfiedDependencies(
        selectedModules,
        packagesForSelectedVersion,
        props.installedPackages || []
    );

    // Type guard function to narrow install.infoMessage from string | undefined to string
    const hasInfoMessage = (install: IUpgradeSchema): boolean =>
        selectedModules[install.packageName] && install.infoMessage != null;

    // Derives license notes for currently selected packages that have an infoMessage.
    // Updates automatically as the user checks/unchecks packages.
    const activeInfoMessages: IExtensionInfoMessage[] = packagesForSelectedVersion
        .filter(hasInfoMessage)
        .map((install) => ({ packageName: install.packageName, message: install.infoMessage! }));

    return (
        <>
            <WelcomeMat
                labels={{
                    title: t("welcome_title"),
                    description: t("welcome_description")
                }}
                variant="splash"
                onRequestClose={() => {
                    if (props.onCloseMat) {
                        props.onCloseMat();
                    }
                }}
                // NOTE: SFWelcomeMat calls onRenderInfoActions() unconditionally for "splash",
                // so it must stay defined and never return undefined or null.
                onRenderInfoActions={() => (
                    selectedPackageNames.length > 0 ? (
                        <div className="slds-text-align_left">
                            <div className="slds-text-heading_small slds-m-bottom_x-small">
                                <strong>{t("welcome_selected_packages_count", {
                                    selected: String(selectedPackageNames.length).padStart(2, '0'),
                                    total: String(packagesForSelectedVersion.length).padStart(2, '0')
                                })}</strong>
                            </div>
                            <ul className="slds-list_vertical slds-text-align_left">
                                {selectedPackageNames.map((name) => (
                                    <li key={name} className="slds-item slds-m-bottom_x-small slds-grid slds-grid_align-start">
                                        <Pill
                                            variant="option"
                                            className="slds-size_small"
                                            labels={{
                                                label: name,
                                                removeTitle: t("welcome_remove_package", { name })
                                            }}
                                            {...(!isDisabledModule(name) && getSelectedDependents(name, selectedModules, packagesForSelectedVersion).length === 0 && {
                                                onRemove: () => handleModuleSelection(name, false)
                                            })}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : <></>
                )}
            >
                <div className="slds-grid slds-grid_vertical">
                    <div className="slds-grid_grow">

                        {/* Error banner shown when the last install attempt failed */}
                        {props.errorMessage && (
                            <div className="slds-notify slds-notify_alert slds-alert_error slds-m-bottom_small" role="alert">
                                <span className="slds-assistive-text">{t("welcome_error_label")}</span>
                                <span className="slds-icon_container slds-icon-utility-error slds-m-right_x-small" />
                                <h2>{props.errorMessage}</h2>
                            </div>
                        )}

                        {/* Selected packages whose dependencies are not available for this version */}
                        {unsatisfiedDependencies.length > 0 && (
                            <div className="slds-notify slds-notify_alert slds-alert_error slds-m-bottom_small" role="alert">
                                <span className="slds-assistive-text">{t("welcome_error_label")}</span>
                                <div>
                                    {unsatisfiedDependencies.map(({ packageName, unsatisfied }) =>
                                        unsatisfied.map((dependency, index) => (
                                            <p key={`${packageName}-${dependency.name}-${index}`}>
                                                {getUnsatisfiedDependencyMessage(t, packageName, dependency)}
                                            </p>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Single Version Selection Section */}
                        <div className="slds-m-bottom_medium">
                            <div className="slds-grid slds-grid_align-start slds-grid_vertical-align-center">
                                <div className="slds-col slds-p-right_small">
                                    <label htmlFor="version-select" className="slds-text-heading_small slds-text-color_weak">
                                        {t("welcome_version_label")}:
                                    </label>
                                </div>
                                <div className="slds-col">
                                    <select
                                        id="version-select"
                                        className="slds-select"
                                        value={selectedVersion}
                                        disabled={isVersionLocked}
                                        onChange={(event) => {
                                            handleVersionChange(event.target.value);
                                        }}
                                    >
                                        {availableVersions.map(({ finalVersion, versionName }) => (
                                            <option key={finalVersion} value={finalVersion}>
                                                {versionName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                    <div className="slds-text-heading_small slds-m-bottom_small">{t("welcome_select_packages")}</div>

                    {packagesForSelectedVersion.map((install: IUpgradeSchema, index: number) => {
                        const packageName = install.packageName;
                        const dependents = getSelectedDependents(packageName, selectedModules, packagesForSelectedVersion);
                        const isDisabled = isDisabledModule(packageName) || dependents.length > 0;

                            return (
                                <Card
                                    key={`${packageName}-${index}`}
                                    heading=""
                                    hasNoHeader
                                    className="slds-m-bottom_small slds-p-around_xxx-small"
                                >
                                    <div className="slds-grid slds-grid_vertical-align-center slds-grid_align-start">
                                        <div className="slds-col slds-p-right_small slds-p-left_medium">
                                            <Checkbox
                                                assistiveText={{ label: packageName }}
                                                checked={selectedModules[packageName] || false}
                                                disabled={isDisabled}
                                                onChange={(evt) => handleModuleSelection(packageName, evt.target.checked)}
                                            />
                                        </div>
                                        <div className="slds-col slds-p-right_small">
                                            <span className="slds-avatar slds-avatar_large">
                                                <img className="slds-avatar__image" src="/logo192.png" alt="logo" />
                                            </span>
                                        </div>
                                        <div className="slds-col">
                                            <div className="slds-text-heading_small">{packageName}</div>
                                            <div className="slds-text-body_small">{install.description || ''}</div>
                                            {dependents.length > 0 && (
                                                <div className="slds-text-body_small slds-text-color_weak">
                                                    {t("welcome_required_by", { names: dependents.join(', ') })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>

                    <div className="slds-grid slds-grid_align-end slds-m-top_medium slds-p-top_small slds-border_top">
                        <Button
                            variant="neutral"
                            label={t("push_portal_cancel")}
                            title={t("push_portal_cancel")}
                            className="slds-m-right_x-small"
                            onClick={() => {
                                if (props.onCloseMat) {
                                    props.onCloseMat();
                                }
                            }}
                        />
                        <Button
                            variant="brand"
                            label={t("task_start_install")}
                            title={t("task_start_install")}
                            disabled={selectedPackageNames.length === 0 || unsatisfiedDependencies.length > 0}
                            onClick={() => {
                                // Defer the actual install call until the user confirms the
                                // org they're installing into via the org info modal below.
                                setIsOrgInfoModalOpen(true);
                            }}
                        />
                    </div>
                </div>
            </WelcomeMat>
            <CQCurrentOrgInfoModal
                alertMode
                modalOpenState={isOrgInfoModalOpen}
                toggleModalState={() => setIsOrgInfoModalOpen(false)}
                onProceed={() => {
                    // toggleModalState (called by the modal's own "Continue" handler before
                    // onProceed) already closes the modal - just kick off the install here.
                    props.onInstall(selectedModules, getSelectedVersionsForInstall());
                }}
                orgInfo={props.orgInfo}
                infoMessage={activeInfoMessages}
            />
        </>
    );
}

export default CQWelcomeMat;
